import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/queryClient'
import { useAuthStore } from '@/stores/authStore'
import type { InventoryPerishable, InventoryNonperishable, PurchaseOrder } from '@/types/app'

export function useInventoryPerishable() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')

  return useQuery({
    queryKey: queryKeys.inventoryPerishable(restaurantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inventory_perishable')
        .select('*, ingredient:ingredients(*)')
        .eq('restaurant_id', restaurantId)
        .order('ingredient(name)', { ascending: true })
      if (error) throw error
      return (data ?? []) as InventoryPerishable[]
    },
    enabled: !!restaurantId,
  })
}

export function useInventoryNonperishable() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')

  return useQuery({
    queryKey: queryKeys.inventoryNonperishable(restaurantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inventory_nonperishable')
        .select('*, ingredient:ingredients(*)')
        .eq('restaurant_id', restaurantId)
        .order('ingredient(name)', { ascending: true })
      if (error) throw error
      return (data ?? []) as InventoryNonperishable[]
    },
    enabled: !!restaurantId,
  })
}

interface UpsertNonperishablePayload {
  ingredient_id: string
  quantity: number
  unit: string
  par_level: number | null
  reorder_point: number | null
}

export function useUpsertNonperishableStock() {
  const qc = useQueryClient()
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')

  return useMutation({
    mutationFn: async (payload: UpsertNonperishablePayload) => {
      const { data, error } = await supabase
        .from('inventory_nonperishable')
        .upsert(
          { ...payload, restaurant_id: restaurantId, counted_at: new Date().toISOString() } as Record<string, unknown>,
          { onConflict: 'restaurant_id,ingredient_id' }
        )
        .select()
        .single()
      if (error) throw error
      return data as InventoryNonperishable
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.inventoryNonperishable(restaurantId) })
    },
  })
}

interface UpsertPerishablePayload {
  ingredient_id: string
  quantity: number
  unit: string
  par_level: number | null
  reorder_point: number | null
  expiry_date: string | null
  location: string | null
  notes: string | null
}

export function useUpsertPerishableStock() {
  const qc = useQueryClient()
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')

  return useMutation({
    mutationFn: async (payload: UpsertPerishablePayload) => {
      const { data, error } = await supabase
        .from('inventory_perishable')
        .upsert(
          { ...payload, restaurant_id: restaurantId, counted_at: new Date().toISOString() } as Record<string, unknown>,
          { onConflict: 'ingredient_id,restaurant_id' }
        )
        .select()
        .single()
      if (error) throw error
      return data as InventoryPerishable
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.inventoryPerishable(restaurantId) })
    },
  })
}

export function usePurchaseOrders() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')

  return useQuery({
    queryKey: queryKeys.purchaseOrders(restaurantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('purchase_orders')
        .select('*, supplier:suppliers(*), items:purchase_order_items(*, ingredient:ingredients(*))')
        .eq('restaurant_id', restaurantId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as PurchaseOrder[]
    },
    enabled: !!restaurantId,
  })
}

interface CreatePOItem {
  ingredient_id: string
  quantity_ordered: number
  unit: string
  unit_price: number | null
  notes: string | null
}

interface CreatePOPayload {
  supplier_id: string | null
  notes: string | null
  items: CreatePOItem[]
}

export function useCreatePurchaseOrder() {
  const qc = useQueryClient()
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')

  return useMutation({
    mutationFn: async (payload: CreatePOPayload) => {
      const { data: po, error: poError } = await supabase
        .from('purchase_orders')
        .insert({
          restaurant_id: restaurantId,
          supplier_id: payload.supplier_id,
          status: 'draft',
          notes: payload.notes,
        } as Record<string, unknown>)
        .select()
        .single()
      if (poError) throw poError

      if (payload.items.length > 0) {
        const { error: itemsError } = await supabase
          .from('purchase_order_items')
          .insert(
            payload.items.map(item => ({
              ...item,
              po_id: po.id,
            })) as Record<string, unknown>[]
          )
        if (itemsError) throw itemsError
      }

      return po as PurchaseOrder
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.purchaseOrders(restaurantId) })
    },
  })
}

interface UpdatePOStatusPayload {
  id: string
  status: PurchaseOrder['status']
}

export function useUpdatePOStatus() {
  const qc = useQueryClient()
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')

  return useMutation({
    mutationFn: async ({ id, status }: UpdatePOStatusPayload) => {
      const { error } = await supabase
        .from('purchase_orders')
        .update({ status } as Record<string, unknown>)
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.purchaseOrders(restaurantId) })
    },
  })
}
